import { ref, nextTick, Ref } from 'vue';
import {
  EditingFilterToken,
  FilterDropdownInfo,
  FilterToken,
} from '@/common/components/molecules/filterSearch/filterSearch.type';

interface UseFilterLayoutParams {
  tokenList: Ref<FilterToken[]>;
  editingToken: Ref<EditingFilterToken | null>;
  isFilterActive: Ref<boolean>;
  activateFilter: () => Promise<void>;
  dropdownListInfo: FilterDropdownInfo;
  hideDropdownList: () => void;
  quitEditingToken: () => Promise<void>;
}

export function useFilterLayout({
  tokenList,
  editingToken,
  isFilterActive,
  activateFilter,
  dropdownListInfo,
  hideDropdownList,
  quitEditingToken,
}: UseFilterLayoutParams) {
  const filterSearchRef = ref();
  const tokensMoreBtnWrapperRef = ref();
  const tokensWrapperRef = ref();
  const mainInputRef = ref();
  const mainInputValue = ref('');
  const moreCount = ref<number>(0);
  const isExpand = ref<boolean>(false);
  const isFocus = ref<boolean>(false);

  const tokenRefs = ref<HTMLElement[]>([]);
  const setElementRef = (el, index: number) => {
    if (el instanceof HTMLElement) {
      tokenRefs.value[index] = el;
    }
  };

  const focusMainInput = async () => {
    isFocus.value = true;
    isExpand.value = true;
    await nextTick();
    mainInputRef.value?.focus({ preventScroll: true });
  };

  const setMoreCount = async () => {
    if (
      !tokensMoreBtnWrapperRef.value ||
      !tokensWrapperRef.value ||
      !tokenRefs.value ||
      !tokenList.value.length
    ) {
      moreCount.value = 0;
      return;
    }

    if (isExpand.value) {
      tokenRefs.value.forEach((item: HTMLElement) => {
        item.style.display = '';
        item.style.maxWidth = '';
      });
      const editingOffset = editingToken.value ? 1 : 0;
      moreCount.value = tokenList.value.length - editingOffset;
      return;
    }

    await nextTick();

    const GAP_BETWEEN_TAG_ITEMS = 5;
    const MINIMUM_TEXT_FIELD_WIDTH = 50;
    const MORE_BTN_RESERVED_WIDTH = 50;

    tokenRefs.value.forEach((item: HTMLElement) => {
      item.style.display = 'list-item';
      item.style.position = 'absolute';
      item.style.visibility = 'hidden';
      item.style.maxWidth = '';
    });

    const containerWidth = tokensMoreBtnWrapperRef.value.getBoundingClientRect().width;
    const widths = tokenRefs.value.map((item: HTMLElement) => item.offsetWidth);

    tokenRefs.value.forEach((item: HTMLElement) => {
      item.style.position = '';
      item.style.visibility = '';
    });

    let count = 0;
    let currentWidth = MINIMUM_TEXT_FIELD_WIDTH;
    let firstOverflowIdx = -1;

    for (let i = 0; i < widths.length; i++) {
      currentWidth += widths[i] + GAP_BETWEEN_TAG_ITEMS;
      if (currentWidth >= containerWidth) {
        firstOverflowIdx = i;
        break;
      }
    }

    if (firstOverflowIdx >= 0) {
      const MIN_TRUNCATED_TOKEN_WIDTH = 100;
      const adjustedWidth = containerWidth - MORE_BTN_RESERVED_WIDTH;

      let usedWidth = MINIMUM_TEXT_FIELD_WIDTH;
      let truncateIdx = -1;

      for (let i = 0; i < widths.length; i++) {
        const nextWidth = usedWidth + widths[i] + GAP_BETWEEN_TAG_ITEMS;
        if (nextWidth >= adjustedWidth) {
          truncateIdx = i;
          break;
        }
        usedWidth = nextWidth;
      }

      if (truncateIdx >= 0) {
        const remaining = adjustedWidth - usedWidth - GAP_BETWEEN_TAG_ITEMS;
        const canTruncate = remaining >= MIN_TRUNCATED_TOKEN_WIDTH || truncateIdx === 0;

        if (canTruncate && tokenRefs.value[truncateIdx]) {
          tokenRefs.value[truncateIdx].style.maxWidth = `${Math.max(remaining, 0)}px`;
          const hideFromIdx = truncateIdx + 1;
          count = widths.length - hideFromIdx;
          for (let j = hideFromIdx; j < tokenRefs.value.length; j++) {
            tokenRefs.value[j].style.display = 'none';
          }
        } else {
          count = widths.length - truncateIdx;
          for (let j = truncateIdx; j < tokenRefs.value.length; j++) {
            tokenRefs.value[j].style.display = 'none';
          }
        }
      }
    }

    moreCount.value = count;
  };

  const expand = async () => {
    if (!isFilterActive.value) {
      await activateFilter();
    }
    isExpand.value = true;
    isFocus.value = true;
    await setMoreCount();
    if (!editingToken.value) {
      await focusMainInput();
    }
  };

  const onClickMoreCount = async () => {
    if (isExpand.value) {
      isExpand.value = false;
      isFocus.value = false;
      hideDropdownList();
      await quitEditingToken();
      await setMoreCount();
    } else {
      await expand();
    }
  };

  const focusPartsInput = (targetTokenID: number) => {
    const targetTokenIndex = tokenList.value?.findIndex((item) => item.id === targetTokenID);
    const targetTokenRef = tokenRefs.value?.[targetTokenIndex] as HTMLElement;
    if (targetTokenRef) {
      const targetInputEl = targetTokenRef?.querySelector('input');
      if (
        targetInputEl &&
        dropdownListInfo.category === 'value' &&
        (!dropdownListInfo.items?.length || dropdownListInfo.isAutoValueItems)
      ) {
        const inputText = tokenList.value[targetTokenIndex].values[0]?.name;
        if (inputText) {
          targetInputEl.value = inputText;
        }
      }
      targetInputEl?.focus({ preventScroll: true });
    }
  };

  return {
    filterSearchRef,
    tokensMoreBtnWrapperRef,
    tokensWrapperRef,
    mainInputRef,
    mainInputValue,
    moreCount,
    isExpand,
    isFocus,
    tokenRefs,
    setElementRef,
    focusMainInput,
    setMoreCount,
    expand,
    onClickMoreCount,
    focusPartsInput,
  };
}
